import React, { useRef, useState } from "react";

interface Props {
  onSend: (message: string) => void;
}

const ChatInput: React.FC<Props> = ({ onSend }) => {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const send = () => {
    if (text.trim()) {
      onSend(text.trim());
      setText("");
      const textarea = textareaRef.current;
      if (textarea) 
        textarea.rows = 1

    }
  };

  const hanldeInput = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.rows = 1; // Reset to 1 row before recalculating
      const lineHeight = 24; // Approximate line height in px
      const maxRows = 4;
      const newRows = Math.min(Math.floor(textarea.scrollHeight / lineHeight), maxRows);
      textarea.rows = newRows;
    }
  }

  return (
    <div className="flex items-end gap-3 p-4 bg-slate-900 border-t border-slate-800 w-full z-10 sticky bottom-0">
      {/* Emoji Button */}
      <button className="p-2.5 mb-1 text-slate-400 hover:text-blue-400 hover:bg-slate-800 transition-all rounded-full">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {/* Attachment Button */}
      <button className="p-2.5 mb-1 text-slate-400 hover:text-blue-400 hover:bg-slate-800 transition-all rounded-full hidden sm:block">
        <svg className="w-6 h-6 transform rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
      </button>
      
      {/* Message input */}
      <div className="flex-1 bg-slate-950 rounded-2xl border border-slate-800 focus-within:border-blue-500/50 focus-within:ring-2 focus-within:ring-blue-500/10 overflow-hidden shadow-inner transition-all min-h-[48px] flex items-center">
        <textarea
          ref={textareaRef}
          className="w-full px-5 py-3 bg-transparent text-white placeholder-slate-500 resize-none focus:outline-none text-[15px] leading-6 max-h-[120px] scrollbar-thin scrollbar-thumb-slate-700"
          placeholder="Type a message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onInput={hanldeInput}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
        />
      </div>
      
      {/* Send/Mic button */}
      <button 
        onClick={send} 
        disabled={!text.trim()}
        className={`p-3 rounded-full transition-all flex-shrink-0 flex items-center justify-center shadow-lg ${
          text.trim() 
            ? 'bg-blue-600 hover:bg-blue-500 text-white transform hover:scale-105 active:scale-95 shadow-blue-500/20' 
            : 'text-slate-600 bg-slate-800/50 cursor-not-allowed'
        }`}
      >
        {text.trim() ? (
            <svg className="w-5 h-5 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
        ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
        )}
      </button>
    </div>
  );
};

export default ChatInput;
